import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router';

import ScrollToTop from '../../routes/ScrollToTop';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { colors } from '../../theme/tokens';

import ReadOnlyGuard from '../../context/ReadOnlyGuard';
import GlobalsReadOnlyBanner from '../Layout/GlobalsReadOnlyBanner';

import AdminSidebar from './AdminSidebar';
import AdminTopBar from './AdminTopBar';
import EnvStrip from './EnvStrip';

const DRAWER_WIDTH = 308;
const ENV_STRIP_HEIGHT = 28;

/**
 * Key we stash tenant round-trip info under in sessionStorage.
 * Shape: `{ tenantCode: string, path: string }`.
 * See F10 — entering /admin from a tenant URL preserves the return path
 * so the AdminTopBar can render a persistent "Return to tenant X" pill.
 * Exported so AdminTopBar (sibling) and any future deep-link helper read
 * the same key without stringly-typed drift.
 */
export const ADMIN_RETURN_TENANT_KEY = 'adminReturnTenant';

export interface AdminReturnTenantStash {
  tenantCode: string;
  path: string;
}

/**
 * Admin view shell. Mirrors `pages/Layout/Layout.tsx` (tenant shell) but
 * with different chrome:
 *   - EnvStrip pinned to the top (slate / env-colored band)
 *   - AdminTopBar instead of TopBar (includes the return-to-tenant pill)
 *   - AdminSidebar instead of the tenant Sidebar
 *
 * Route guard: `(!isSuperAdmin || !adminViewEnabled)` → redirect to `/`.
 * While `/api/me` is still resolving we render a spinner rather than
 * bouncing — otherwise super-admins briefly see the tenant picker on a
 * direct `/admin` hit before `isSuperAdmin` flips true.
 */
const AdminLayout: React.FC = () => {
  const location = useLocation();
  // Per-env OPERATIONAL admin routes — mirror of the server's
  // DATA_OP_WRITE_PREFIXES (/api/admin/users, /api/admin/rbac): these manage
  // env-local data and are editable on EVERY env, so the globals read-only
  // banner/guard must not apply to them.
  const isAdminOpsRoute = /^\/admin\/(users|rbac)(\/|$)/.test(location.pathname);
  const { isSuperAdmin, adminViewEnabled, isLoading } = useCurrentUser();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // If the user navigated here from a tenant page (React Router passes
  // the source via `location.state`), stash the return path. We don't
  // clobber an existing stash — the first tenant URL seen wins, so an
  // admin → admin subroute navigation doesn't forget the original tenant.
  useEffect(() => {
    const fromTenant = (location.state as { tenantCode?: string; path?: string } | null) ?? null;
    if (!fromTenant?.tenantCode) return;
    try {
      const existing = window.sessionStorage.getItem(ADMIN_RETURN_TENANT_KEY);
      if (existing) return;
      const stash: AdminReturnTenantStash = {
        tenantCode: fromTenant.tenantCode,
        path: fromTenant.path || `/tenants/${fromTenant.tenantCode}`,
      };
      window.sessionStorage.setItem(ADMIN_RETURN_TENANT_KEY, JSON.stringify(stash));
    } catch {
      /* storage unavailable — return pill simply won't render */
    }
  }, [location.state]);

  // Close mobile drawer on any navigation (same UX as tenant layout).
  useEffect(() => {
    if (isCompact) setMobileOpen(false);
  }, [location.pathname, isCompact]);

  // /api/me is still resolving — don't navigate yet. A brief flash of
  // spinner is strictly better than bouncing a super-admin to `/` and
  // back on every direct /admin load.
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: colors.brand }} />
      </Box>
    );
  }

  // Kill-switch / role guard. Per F23.3 the two flags are AND'd — a
  // super-admin whose env has `ADMIN_VIEW_ENABLED=false` still bounces.
  if (!isSuperAdmin || !adminViewEnabled) {
    return <Navigate to="/" replace />;
  }

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    p: 3,
    pb: 0,
    borderRight: `1px solid ${colors.borderLight}`,
    // Push sidebar below the fixed env strip so it doesn't hide under it.
    top: ENV_STRIP_HEIGHT,
    height: `calc(100% - ${ENV_STRIP_HEIGHT}px)`,
  };

  const sidebar = <AdminSidebar onClose={isCompact ? () => setMobileOpen(false) : undefined} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <EnvStrip />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {isCompact ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
          >
            {sidebar}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              '& .MuiDrawer-paper': drawerPaperSx,
            }}
          >
            {sidebar}
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: colors.bgPage,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: isCompact ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
          }}
        >
          <AdminTopBar
            isCompact={isCompact}
            onOpenDrawer={() => setMobileOpen(true)}
          />

          {/* WAVE H — the admin surface is the GLOBAL / catalog pages. Their
              editability follows the globals-everywhere policy (editable on
              every non-prod env when the flag is on; dev01-only when off), NOT
              the per-tenant home gate. Banner + guard both consult globals
              editability via scope="globals". Flag-off → byte-identical to the
              legacy env-level read-only behavior these pages get from the
              backend write-fence.

              EXCEPTION — per-env OPERATIONAL pages (Users, RBAC): app_user /
              app_role are env-local operational data (who can access THIS env's
              portal), NOT the config.* catalog the promotion model protects.
              The server already exempts their writes from the read-only-ENV
              fence (index.ts DATA_OP_WRITE_PREFIXES: /api/admin/users,
              /api/admin/rbac) — the FE guard/banner must match, or the pages
              read as locked on every non-dev01 env while the API would happily
              accept the write. Access is still gated by super-admin +
              ADMIN_VIEW_ENABLED inside the admin router. */}
          {isAdminOpsRoute ? null : <GlobalsReadOnlyBanner />}

          <Box sx={{ flex: 1, p: isCompact ? 2 : 4, pt: 2 }}>
            <ScrollToTop />
            {isAdminOpsRoute ? (
              <Outlet />
            ) : (
              <ReadOnlyGuard scope="globals">
                <Outlet />
              </ReadOnlyGuard>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
